import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AppShell from './components/layout/AppShell';
import LoginPage from './components/auth/LoginPage';
import AuthCallback from './components/auth/AuthCallback';
import WaysCallback from './components/auth/WaysCallback';
import { useAuthStore } from './store/useAuthStore';
import { startTokenRefreshScheduler, stopTokenRefreshScheduler } from './services/auth';
import { useProjectStore } from './store/useProjectStore';
import { useConfigStore } from './store/useConfigStore';
import { useFundingStore } from './store/useFundingStore';
import { useFunderStore } from './store/useFunderStore';
import { useResourceStore } from './store/useResourceStore';
import { useOrgHierarchyStore } from './store/useOrgHierarchyStore';
import { useDashboardConfigStore } from './store/useDashboardConfigStore';
import { usePIStore } from './store/usePIStore';

export default function App() {
  const { user, loading, initialize } = useAuthStore();
  const fetchProjects = useProjectStore((s) => s.fetchProjects);
  const fetchConfig = useConfigStore((s) => s.fetchConfig);
  const fetchWorkflowStatuses = useConfigStore((s) => s.fetchWorkflowStatuses);
  const fetchOpportunities = useFundingStore((s) => s.fetchOpportunities);
  const fetchSummary = useFundingStore((s) => s.fetchSummary);
  const fetchFunders = useFunderStore((s) => s.fetchFunders);
  const fetchResources = useResourceStore((s) => s.fetchResources);
  const fetchOrganizations = useOrgHierarchyStore((s) => s.fetchOrganizations);
  const fetchDashboardConfig = useDashboardConfigStore((s) => s.fetchConfig);
  const fetchPIs = usePIStore((s) => s.fetchPIs);
  const fetchAllSprints = usePIStore((s) => s.fetchAllSprints);

  // Initialize auth on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Start / stop proactive token refresh based on auth state
  useEffect(() => {
    if (user) {
      startTokenRefreshScheduler();
    } else {
      stopTokenRefreshScheduler();
    }
    return () => stopTokenRefreshScheduler();
  }, [user]);

  // Fetch app data once authenticated
  useEffect(() => {
    if (user) {
      fetchProjects();
      fetchConfig();
      fetchWorkflowStatuses();
      fetchOrganizations();
      fetchDashboardConfig();
      fetchPIs();
      fetchAllSprints();
      // Only fetch funding/funder/resource data for users with access
      const role = user.pmo_role;
      if (role === 'super_admin' || role === 'project_admin') {
        fetchOpportunities();
        fetchSummary();
        fetchFunders();
        fetchResources();
      }
    }
  }, [user, fetchProjects, fetchConfig, fetchOpportunities, fetchSummary, fetchFunders, fetchResources, fetchOrganizations, fetchDashboardConfig, fetchPIs, fetchAllSprints]);

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* The Ways sign-in's return leg (PO-92 F5.1): only for someone signed in to PMO. */}
      <Route path="/auth/ways/callback" element={user ? <WaysCallback /> : <Navigate to="/login" replace />} />

      {/* Protected routes — redirect to login if not authenticated */}
      <Route path="/*" element={user ? <AppShell /> : <Navigate to="/login" replace />} />
    </Routes>
  );
}
